export type MonitorStatsWorkflow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  executionCount: number;
  lastRunAt: number | null;
};

export type WorkflowExecutionCountRow = {
  workflowId: string;
  spaceId: string;
  name: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  executionCount: number;
  lastRunAt: number | null;
};

export type SpaceExecutionCountRow = {
  spaceId: string;
  executionCount: number;
  workflowCount: number;
};

// A null lastRunAt means the window holds no rollup row at all, which keeps the
// workflow out of the list rather than listing it at zero.
export function computeWorkflowExecutionCountRows(
  workflows: ReadonlyArray<MonitorStatsWorkflow>
): WorkflowExecutionCountRow[] {
  const rows: WorkflowExecutionCountRow[] = [];

  for (const workflow of workflows) {
    if (workflow.lastRunAt == null) continue;

    rows.push({
      workflowId: workflow.id,
      spaceId: workflow.spaceId,
      name: workflow.name,
      badgeSeed: workflow.badgeSeed,
      pausedAt: workflow.pausedAt,
      executionCount: workflow.executionCount,
      lastRunAt: workflow.lastRunAt,
    });
  }

  return rows.sort(
    (a, b) =>
      b.executionCount - a.executionCount || a.name.localeCompare(b.name)
  );
}

export function computeSpaceExecutionCountRows(
  workflowRows: readonly WorkflowExecutionCountRow[],
  spaceIds: readonly string[]
): SpaceExecutionCountRow[] {
  const bySpace = new Map<
    string,
    { executionCount: number; workflowCount: number }
  >();
  for (const spaceId of spaceIds) {
    bySpace.set(spaceId, { executionCount: 0, workflowCount: 0 });
  }

  for (const row of workflowRows) {
    const entry = bySpace.get(row.spaceId);
    if (!entry) continue;
    entry.executionCount += row.executionCount;
    entry.workflowCount += 1;
  }

  const rows: SpaceExecutionCountRow[] = [];
  for (const [spaceId, data] of bySpace) {
    rows.push({ spaceId, ...data });
  }

  return rows
    .filter((row) => row.executionCount > 0)
    .sort(
      (a, b) =>
        b.executionCount - a.executionCount ||
        a.spaceId.localeCompare(b.spaceId)
    );
}

export function formatExecutionCount(count: number): string {
  return count.toLocaleString("en-US");
}

// Bars are scaled logarithmically so spaces spanning several orders of magnitude
// stay visible and comparable.
export function executionCountBarFraction(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  if (max === 1) return 1;
  return Math.log1p(count) / Math.log1p(max);
}

export function workflowRowsForSpace(
  workflowRows: readonly WorkflowExecutionCountRow[],
  spaceId: string
): WorkflowExecutionCountRow[] {
  return workflowRows
    .filter((row) => row.spaceId === spaceId)
    .sort(
      (a, b) =>
        b.executionCount - a.executionCount || a.name.localeCompare(b.name)
    );
}
